/**
 * The one interactive piece of the page: choose an ORM and a database and
 * every example on the page shows that stack's real warning and fix.
 */
import {
  CODE,
  DATABASES,
  configLines,
  ORMS,
  escapeHtml,
  highlight,
  showcase,
  type DbKey,
  type OrmKey,
} from "./showcase.ts";
import { setUpTour } from "./config-tour.ts";
import "./motion.ts";

interface State {
  orm: OrmKey;
  db: DbKey;
}

const state: State = { orm: "prisma", db: "postgresql" };

function render(): void {
  const code = CODE[state.orm];
  const fix = showcase(state.orm, state.db);

  setHtml(
    "[data-code]",
    code.lines
      .map(
        (line, i) =>
          `<span class="line"><span class="ln">${code.firstLine + i}</span>${highlight(line)}</span>`,
      )
      .join(""),
  );
  setText("[data-file]", code.file);
  for (const editor of document.querySelectorAll<HTMLElement>(".editor")) {
    editor.dataset.orm = state.orm;
  }
  setText("[data-message]", fix.message);
  setText("[data-ratio]", fix.ratio);
  setHtml(
    "[data-config]",
    configLines(state.orm)
      .map((line) => `<span class="line">${highlight(line)}</span>`)
      .join(""),
  );
  setText("[data-detail]", `${fix.detail} Confidence: high.`);
  setText("[data-fix-title]", fix.title);

  setText("[data-schema-file]", fix.schemaFile);
  setHtml(
    "[data-schema]",
    fix.schema.map((line) => `<span class="line">${highlight(line)}</span>`).join(""),
  );
  setText(
    "[data-migration-file]",
    fix.migrationFile ?? "Online SQL, to run by hand on a busy table",
  );
  setHtml("[data-sql]", escapeHtml(fix.sql));
  setText("[data-follow-up]", fix.followUp);

  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-orm]")) {
    button.setAttribute("aria-pressed", String(button.dataset.orm === state.orm));
  }
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-db]")) {
    button.setAttribute("aria-pressed", String(button.dataset.db === state.db));
  }
}

function setText(selector: string, text: string): void {
  for (const node of document.querySelectorAll(selector)) node.textContent = text;
}

function setHtml(selector: string, html: string): void {
  for (const node of document.querySelectorAll(selector)) node.innerHTML = html;
}

document.addEventListener("click", (event) => {
  const target = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-orm], [data-db]");
  if (!target) return;
  if (target.dataset.orm && ORMS.some((o) => o.key === target.dataset.orm))
    state.orm = target.dataset.orm as OrmKey;
  if (target.dataset.db && DATABASES.some((d) => d.key === target.dataset.db))
    state.db = target.dataset.db as DbKey;
  render();
});

render();

// The headline cycles through what Plinth saves you. It holds still for
// reduced motion, while the tab is hidden, and while the pointer is on it.
function rotateHeadline(): void {
  const rotator = document.querySelector<HTMLElement>("[data-rotator]");
  if (!rotator || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const phrases = [...rotator.children];
  let current = 0;
  let paused = false;
  rotator.addEventListener("pointerenter", () => (paused = true));
  rotator.addEventListener("pointerleave", () => (paused = false));
  setInterval(() => {
    if (paused || document.hidden) return;
    phrases[current]?.classList.remove("is-current");
    current = (current + 1) % phrases.length;
    phrases[current]?.classList.add("is-current");
  }, 3200);
}

rotateHeadline();

// Light or dark: follows the system until the visitor picks one, then remembers it.
const darkQuery = matchMedia("(prefers-color-scheme: dark)");

function isDark(): boolean {
  const chosen = document.documentElement.dataset.theme;
  return chosen ? chosen === "dark" : darkQuery.matches;
}

function labelThemeToggle(): void {
  for (const button of document.querySelectorAll("[data-theme-toggle]")) {
    button.setAttribute("aria-label", isDark() ? "Switch to light mode" : "Switch to dark mode");
  }
}

document.addEventListener("click", (event) => {
  if (!(event.target as HTMLElement).closest("[data-theme-toggle]")) return;
  const theme = isDark() ? "light" : "dark";
  document.documentElement.dataset.theme = theme;
  try {
    localStorage.setItem("plinth-theme", theme);
  } catch {
    // Storage blocked: the choice lasts until the page is closed.
  }
  labelThemeToggle();
});
darkQuery.addEventListener("change", labelThemeToggle);
labelThemeToggle();

// The load animation plays once; later switches update in place.
setTimeout(() => document.documentElement.setAttribute("data-settled", ""), 2000);

setUpTour();
